'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type IQueueStatusData,
  type IQueueStatusVar,
  QUEUE_STATUS,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useDisclosure } from '@/core/hooks';
import { ErrorFeedback, Heading, SuccessDataFeedback, SuccessFeedback } from '@/core/molecules';
import { Alert, AnimatedComponent, Button, Confirm, cn, FormSelect, FormTextArea } from '@/core/ui';
import { DeleteRecordsDocument } from '@/graphql/gql/graphql';
import { useMutation } from '@/libs';
import { getSelectItem, stringToNumberArr } from '@/utils';

enum ERecordType {
  QUESTION = 0,
  STATION = 1,
  CONCEPT = 5,
  CHOICE = 8,
}

const batchTypes = getSelectItem(ERecordType as never, [
  ERecordType.QUESTION,
  ERecordType.STATION,
  ERecordType.CONCEPT,
  ERecordType.CHOICE,
]);

const schema = z.object({
  reason: z.string().min(1, 'Reason is required'),
  type: z.enum(ERecordType, { message: 'Invalid enum value' }),
  recordIds: z
    .string()
    .min(1, 'Ids are required')
    .regex(/^[\d\r\n\s,]+$/, 'Ids must only contain numbers, comma and whitespace'),
});

type ISchema = z.infer<typeof schema>;

const DeleteRecord = ({ className }: { className?: string }) => {
  const {
    control,
    getValues,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [deleteRecords, { loading, data }] = useMutation(DeleteRecordsDocument);
  const finalData = useMemo(() => data?.admin?.deleteRecords, [data?.admin?.deleteRecords]);

  const onSubmit: SubmitHandler<ISchema> = async () => {
    onOpen();
  };

  const onConfirm = async () => {
    const input = getValues();
    await deleteRecords({
      variables: { input: { ...input, recordIds: stringToNumberArr(input.recordIds) } },
    });
  };

  return (
    <>
      <Confirm
        onConfirm={onConfirm}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title='Delete Record'
        message='Are you sure want to proceed?'
      />
      <div className={cn('flex flex-col space-y-2 justify-center w-full items-center', className)}>
        <div className='bg-background rounded-lg shadow-md w-full z-2'>
          <Heading title='Create New Delete Queue' />

          <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
            <Alert
              type='warn'
              className='mb-8'
              message='Please read below before proceeding!'
              list={[
                'Make sure correct DB storage is selected',
                'Records will be deleted next day during low traffic hours!',
                'Check Queue status in right section',
                'As of now, manually remove the deleted data from algolia',
              ]}
            />
            <div className='grid grid-cols-2 items-start gap-y-6 md:gap-y-10 gap-x-8'>
              <FormTextArea
                isRequired={true}
                description='Enter comma seperated ids you want to delete'
                control={control}
                name='recordIds'
                labelPlacement='outside'
                placeholder='1,2'
                className='rounded col-span-2 md:col-span-1'
                variant='faded'
                label='Record Id'
              />
              <FormTextArea
                isRequired={true}
                description='Enter Reason for removing those records'
                control={control}
                name='reason'
                labelPlacement='outside'
                placeholder=''
                className='rounded col-span-2 md:col-span-1'
                variant='faded'
                label='Reason'
              />
              <FormSelect
                isRequired={true}
                description='Carefully select the type of record'
                options={batchTypes}
                control={control}
                transform={(a) => Number.parseInt(a, 10)}
                labelPlacement='outside'
                name='type'
                placeholder='Select Type'
                className='rounded col-span-2 md:col-span-1'
                variant='faded'
                label='Type'
              />
            </div>
            <div className='flex justify-center mt-10'>
              <Button
                isDisabled={!isValid}
                isLoading={loading}
                className='w-full max-w-xl bg-primary'
                type='submit'
                radius='sm'
                size='lg'
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
        <AnimatedComponent>
          {finalData && (
            <motion.div
              initial={{ y: -160 }}
              animate={{ y: 0 }}
              exit={{ y: -160 }}
              transition={{ type: 'spring' }}
              className={cn(
                'flex flex-col space-y-4 justify-center items-center',
                'w-full py-5',
                'bg-background rounded-lg shadow-md ',
              )}
            >
              {finalData?.status === 200 ? (
                <SuccessFeedback
                  symbol='Queue Ids :'
                  message={finalData?.message || ''}
                  response={finalData as any}
                />
              ) : (
                <ErrorFeedback response={finalData as any} />
              )}
            </motion.div>
          )}
        </AnimatedComponent>
      </div>
    </>
  );
};

const DeleteStatus = ({ className }: { className?: string }) => {
  const schema = z.object({
    ids: z
      .string()
      .min(1, 'Ids are required')
      .regex(/^[\d\r\n\s,]+$/, 'Ids must only contain numbers, comma and whitespace'),
  });

  type ISchema = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const [queueStatus, { loading, data }] = useMutation<IQueueStatusData, IQueueStatusVar>(QUEUE_STATUS);

  const finalData = useMemo(() => data?.admin?.queueStatus, [data?.admin?.queueStatus]);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    await queueStatus({ variables: { ids: stringToNumberArr(input.ids) } });
  };

  return (
    <div className={cn('flex flex-col space-y-2 justify-center w-full items-center', className)}>
      <div className='bg-background rounded-lg shadow-md w-full z-2'>
        <Heading title='Check Queue Status' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-4 md:p-6'>
          <div className='grid grid-cols-2'>
            <FormTextArea
              isRequired={true}
              description=''
              control={control}
              name='ids'
              labelPlacement='outside'
              placeholder='1,2'
              className='rounded col-span-2'
              variant='faded'
              label='Queue Ids'
            />
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full bg-primary'
              type='submit'
              radius='sm'
              size='md'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full py-5 px-8',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData?.status === 200 ? (
              <SuccessDataFeedback message={finalData?.message} response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

const Delete = () => {
  return (
    <div className='flex w-full justify-center h-full'>
      <div className='grid grid-cols-6 w-full items-start gap-8 container'>
        <DeleteRecord className='col-span-6 xl:col-span-4' />
        <DeleteStatus className='col-span-6 xl:col-span-2' />
      </div>
    </div>
  );
};

export default Delete;
